import { Component, OnInit, ViewEncapsulation } from '@angular/core'
import { Title } from '@angular/platform-browser'
import { Observable } from 'rxjs'
import { filter, first, skip, switchMap, takeUntil } from 'rxjs/operators'
import { TranslateService } from '@ngx-translate/core'

import { AuthService } from './modules/auth/auth.service'
import { SidebarService } from '@core/modules/sidebar/sidebar.service'
import { ThemeService } from '@shared/services/theme.service'
import { negativeBoolean } from '@core/helpers/negative-boolean'
import { PlatformSettings } from '@shared/models/platform-settings.model'
import { User } from '@shared/models/user.model'
import { DestroyService } from '@core/services/destroy.service'
import { PlatformSettingsService } from '@core/services/platfrom-settings.service'
import { NetworkStatusService } from '@core/services/network-status.service'
import { Theme } from '@shared/models/theme.model'
import { ClientStatus } from '@shared/models/client.model'
import { SettingsService } from './shared/common-components/settings/settings.service'
import { CrispService } from './modules/auth/crisp.service'
import { Router } from '@angular/router'

@Component({
  selector: 'lin-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [DestroyService, NetworkStatusService],
  standalone: false,
})
export class AppComponent implements OnInit {
  logoSrc: string | null = null
  favIcon: HTMLLinkElement = document.querySelector('#appIcon')
  platformSettings$: Observable<PlatformSettings>

  get currentUser(): User {
    return this.authService.getCurrentUser()
  }

  get showBanner(): boolean {
    // const isGlobalBannerVisible = this.isGlobalMessageEnabled() // banner show even in logout

    if (!this.currentUser) {
      return false // banner not show in logout
      //return isGlobalBannerVisible // banner show even in logout
    }

    const isClientStatusBannerVisible =
      this.currentUser.isClient() &&
      [ClientStatus.PartiallyBlockedBilling, ClientStatus.BlockedBilling].includes(this.currentUser?.team?.clientStatus) &&
      this.settingsService.areBillingFunctionalitiesEnabled

    const isGlobalBannerVisible = this.isGlobalMessageEnabled() // banner not show in logout
    return isClientStatusBannerVisible || isGlobalBannerVisible
  }

  private isGlobalMessageEnabled(): boolean {
    return false
  }

  constructor(
    private titleService: Title,
    public authService: AuthService,
    private sidebarService: SidebarService,
    private themeService: ThemeService,
    private readonly destroyService: DestroyService,
    private readonly platformSettingsService: PlatformSettingsService,
    private readonly settingsService: SettingsService,
    private crispService: CrispService,
    private router: Router,
    private readonly translate: TranslateService,
  ) {
    translate.setDefaultLang('en')
  }

  ngOnInit() {
    this.initThemeSubscriptions()
    this.applyPlatformSetting()
    this.currentUser?.language && this.translate.use(this.currentUser.language)
    this.authService.isLoggedIn$.pipe(filter(Boolean), first()).subscribe(() => this.initializeCrisp())
  }

  toggleSidebar() {
    this.sidebarService.getSidebar().toggleOpen()
  }

  private initThemeSubscriptions() {
    this.authService.isLoggedIn$
      .pipe(
        skip(1),
        filter(Boolean),
        switchMap(() => this.themeService.applyTheme()),
        takeUntil(this.destroyService),
      )
      .subscribe()

    this.authService.isLoggedIn$.pipe(filter(negativeBoolean)).subscribe(() => this.themeService.discardTheme())

    this.themeService.currentTheme$.pipe(filter(Boolean)).subscribe(({ logoSrc }: Theme) => (this.logoSrc = logoSrc))
    this.platformSettings$ = this.platformSettingsService.platformSettings$
  }

  private applyPlatformSetting(): void {
    this.platformSettingsService
      .loadPlatformSettings()
      .pipe(
        switchMap(() => this.platformSettingsService.platformSettings$),
        takeUntil(this.destroyService),
      )
      .subscribe((settings: PlatformSettings) => {
        const { faviconUrl, productName } = settings
        this.favIcon.href = faviconUrl
        this.titleService.setTitle(productName)
      })
  }

  initializeCrisp(): void {
    this.authService.fetchCrispCompanyStats().subscribe({
      next: (response: { data: unknown }) => {
        this.crispService.initCrisp(response.data)
      },
      error: () => {
        // console.error('Error fetching company stats for Crisp:', err);
      },
    })
  }
}
